import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable, Subject } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createFuelPoint, updateFuelPoint } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { FccPmpCfgProfiles, SiteDeviceFccDTO } from "src/app/models/Sites/siteFccDTO";
import { OperationModeDTO, PumpType, SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { NozzleDTO } from "src/app/models/site-devices/NozzleDTO";
import { SiteGradeDTO } from "src/app/models/Sites/siteGradeDTO";
import { SiteWarehouseDTO } from "src/app/models/Sites/siteWarehouseDTO";
import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";
import { FormUtils } from "src/app/shared/utils/formUtils";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";

import { CanComponentDeactivate } from "src/app/shared";
import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { validOperationModesSelection } from "./fuelpoint-form-validators";
import { RolePermissionsService } from "src/app/shared/services/role-permissions/role-permissions.service";
import { filter, switchMap, take, takeUntil } from "rxjs/operators";

const FUELPOINT_FORM_PARAMS = {
  enabled: "enabled",
  siteCode: "siteCode",
  pumpNumber: "pumpNumber",
  serialNumber: "serialNumber",
  description: "description",
  protocolTypeCode: "protocolTypeCode",
  pmpTypeProfile: "pmpTypeProfile",
  nozzles: "nozzles",
  nozzleNumber: "nozzleNumber",
  gradeCode: "gradeCode",
  warehouseCode: "warehouseCode",
  protocol: "protocol",
  operationModes: "operationModes",
};

const MAX_NOZZLES_NR = 8;

@Component({
  selector: "app-fuelpoint-form-dialog",
  templateUrl: "./fuelpoint-form-dialog.component.html",
  styleUrls: ["./fuelpoint-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class FuelPointFormDialogComponent
  implements OnInit, CanComponentDeactivate
{
  @Output() getFuelPointForm: EventEmitter<FormGroup> =
    new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;
  protected destroy$ = new Subject<void>();

  // Permissions
  public canWrite$: Observable<boolean>;
  canWrite: boolean = false;

  siteCode: string;
  currentFuelPoint: SiteFuelPointDTO;

  fuelPointDataForm: FormGroup;

  isCreate = false;

  siteGradeList: SiteGradeDTO[];
  warehouseList: SiteWarehouseDTO[];
  pumpTypeList: PumpType[];
  fccList: SiteDeviceFccDTO[];

  fccPmpCfgProfiles = FccPmpCfgProfiles;

  disableProtocol = false;

  readonly MAX_NOZZLES_NR = MAX_NOZZLES_NR;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<FuelPointFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    public rolePermissionsService: RolePermissionsService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createFuelPointForm();
    this.validateFormValues();
    this.checkPermissions();
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentFuelPoint = this.data.currentFuelPoint;
    this.siteGradeList = this.data.siteGradeList;
    this.warehouseList = this.data.warehouseList.filter(
      (warehouse: SiteWarehouseDTO) => warehouse.siteProduct?.isFuel
    );
    this.pumpTypeList = this.data.pumpTypeList;
    this.fccList = this.data.fccList;
  }

  createFuelPointForm() {
    this.fuelPointDataForm = this.fb.group({
      [FUELPOINT_FORM_PARAMS.enabled]: [true],
      [FUELPOINT_FORM_PARAMS.pumpNumber]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [FUELPOINT_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [FUELPOINT_FORM_PARAMS.description]: [
        null,
        [
          Validators.required,
          FormUtils.noWhitespaceValidator,
          Validators.maxLength(45),
        ],
      ],
      [FUELPOINT_FORM_PARAMS.operationModes]: [
        null,
        validOperationModesSelection(),
      ],
      [FUELPOINT_FORM_PARAMS.protocolTypeCode]: [null, [Validators.required]],
      [FUELPOINT_FORM_PARAMS.pmpTypeProfile]: [{ value: null, disabled: true }],
      [FUELPOINT_FORM_PARAMS.protocol]: this.fb.group({}),
      [FUELPOINT_FORM_PARAMS.nozzles]: this.fb.array(
        [this.createNozzleFormGroup()],
        [Validators.required]
      ),
    });
    this.onProtocolTypeCodeChanges(this.fuelPointDataForm);
    this.subscribeFormChanges(this.fuelPointDataForm);
  }

  checkPermissions(): void {
    this.rolePermissionsService["roles$"]
      .pipe(
        filter((roles) => roles.length > 0),
        take(1),
        switchMap(() =>
          this.rolePermissionsService.hasPermission("SITE_MANAGEMENT", "WRITE")
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((canEdit) => {
        this.canWrite = canEdit;
        if (!canEdit) {
          this.setFormValues(this.currentFuelPoint);
          setTimeout(() => {
            this.disableFormControls(this.fuelPointDataForm);
          });
        }
      });

    this.canWrite$ = this.rolePermissionsService.hasPermission(
      "SITE_MANAGEMENT",
      "WRITE"
    );
  }

  protected disableFormControls(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup) {
        this.disableFormControls(control); // Desativa filhos primeiro
      }

      control?.disable(); // Desativa o próprio
    });
  }

  onProtocolTypeCodeChanges(form: FormGroup): void {
    form
      .get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
      .valueChanges.subscribe((protocolTypeCode) => {
        if (
          protocolTypeCode &&
          (protocolTypeCode === FccPmpCfgProfiles[1].code ||
            protocolTypeCode === FccPmpCfgProfiles[7].code)
        ) {
          form
            .get(FUELPOINT_FORM_PARAMS.pmpTypeProfile)
            .setValidators([Validators.required]);
          form.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).enable();
          this.disableProtocol = true;
        } else {
          form.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).clearValidators();
          form
            .get(FUELPOINT_FORM_PARAMS.pmpTypeProfile)
            .updateValueAndValidity();
          form.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).setValue(null);
          form.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).disable();
          this.disableProtocol = false;
        }
      });
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getFuelPointFormPristine();
    });
  }

  validateFormValues() {
    if (this.isCreate) {
      this.fuelPointDataForm
        .get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
        .enable();
      this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).enable();
      this.disableProtocol = false;
    } else {
      this.setFormValues(this.currentFuelPoint);
      this.setNozzlesFormArray(this.currentFuelPoint);
      this.validateFccFuelPoints(this.currentFuelPoint);
    }
  }

  setFormValues(fuelpoint: SiteFuelPointDTO, duplication?: boolean) {
    this.fuelPointDataForm.controls.enabled.setValue(
      fuelpoint?.enabled ?? false
    );
    this.fuelPointDataForm.controls.pumpNumber.setValue(
      duplication ? null : fuelpoint?.pumpNumber ?? null
    );
    this.fuelPointDataForm.controls.serialNumber.setValue(
      duplication ? null : fuelpoint?.serialNumber ?? null
    );
    this.fuelPointDataForm.controls.description.setValue(
      fuelpoint?.description ?? null
    );
    this.fuelPointDataForm.controls.operationModes.setValue(
      fuelpoint?.operationModes ?? null
    );
    this.fuelPointDataForm.controls.protocolTypeCode.setValue(
      fuelpoint?.protocolTypeCode ?? null
    );
    switch (fuelpoint?.protocolTypeCode) {
      case FccPmpCfgProfiles[1].code:
        this.fuelPointDataForm.controls.pmpTypeProfile.setValue(
          FccPmpCfgProfiles[1].profile.find(
            ({ baudRate }) =>
              baudRate === fuelpoint.communicationMethodData?.baudRate
          )?.id
        );
        break;
      case FccPmpCfgProfiles[7].code:
        this.fuelPointDataForm.controls.pmpTypeProfile.setValue(
          FccPmpCfgProfiles[7].profile.find(
            ({ id }) => id === fuelpoint.communicationMethodData?.address
          )?.id
        );
        break;
      default:
        break;
    }

    setProtocolFormValues(this.fuelPointDataForm, fuelpoint);
  }

  setNozzlesFormArray(fuelpoint: SiteFuelPointDTO) {
    const nozzles = this.getFormArray(FUELPOINT_FORM_PARAMS.nozzles);
    this.clearFormArray(nozzles);

    fuelpoint.nozzles.forEach(({ nozzleNumber, warehouse, grade }) => {
      const { code: warehouseCode } = warehouse;
      const { code: gradeCode } = grade;
      nozzles.push(
        this.createNozzleFormGroup(nozzleNumber, warehouseCode, gradeCode)
      );
    });
  }

  createNozzleFormGroup(
    nozzleNumber?: number,
    warehouseCode?: string,
    gradeCode?: string
  ): FormGroup {
    return this.fb.group({
      [FUELPOINT_FORM_PARAMS.nozzleNumber]: [
        nozzleNumber || nozzleNumber === 0 ? nozzleNumber : null,
        [Validators.required, Validators.min(0), Validators.max(255)],
      ],
      [FUELPOINT_FORM_PARAMS.warehouseCode]: [
        warehouseCode ? warehouseCode : null,
        [Validators.required],
      ],
      [FUELPOINT_FORM_PARAMS.gradeCode]: [
        gradeCode ? gradeCode : null,
        [Validators.required],
      ],
    });
  }

  addNozzle() {
    const nozzles = this.getFormArray(FUELPOINT_FORM_PARAMS.nozzles);
    nozzles.push(this.createNozzleFormGroup());
  }

  removeNozzle(index: number) {
    const nozzles = this.getFormArray(FUELPOINT_FORM_PARAMS.nozzles);
    nozzles.removeAt(index);
    this.fuelPointDataForm.markAsDirty();
  }

  getFormArray(formParam: string): FormArray {
    return this.fuelPointDataForm.get(formParam) as FormArray;
  }

  addDefaultFormArray(formArray: FormArray) {
    formArray.push(this.createNozzleFormGroup());
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
  }

  validateFccFuelPoints(fuelPoint: SiteFuelPointDTO) {
    const fccAsssociatedFuelPoints = this.fccList.reduce((acc, cur) => {
      const { connectedFuelPoint } = cur;
      const fccFuelPoint = connectedFuelPoint.find(
        (fuelPointCode) => fuelPointCode === fuelPoint.code
      );
      if (fccFuelPoint) {
        return [...acc, fccFuelPoint];
      }
      return acc;
    }, []);

    if (fccAsssociatedFuelPoints.length) {
      this.fuelPointDataForm
        .get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
        .disable();
      this.fuelPointDataForm
        .get(FUELPOINT_FORM_PARAMS.pmpTypeProfile)
        .disable();
    } else {
      this.fuelPointDataForm
        .get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
        .enable();
      this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).enable();
    }
  }

  duplicate() {
    this.setFormValues(this.currentFuelPoint, true);

    this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.protocolTypeCode).enable();
    this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.pmpTypeProfile).enable();

    this.isCreate = true;
    this.currentFuelPoint = null;
  }

  onSubmit() {
    if (!this.fuelPointDataForm.valid) {
      this.fuelPointDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    if (this.fuelPointDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    if (this.invalidNozzles(this.fuelPointDataForm)) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.duplicatedNozzles"),
        type: SnackbarType.ERROR,
      });
      return;
    }

    const protocolFormGroup = this.fuelPointDataForm.get(
      FUELPOINT_FORM_PARAMS.protocol
    ) as FormGroup;
    let fuelpoint: SiteFuelPointDTO = {
      siteCode: this.siteCode ? this.siteCode : null,
      code: this.currentFuelPoint?.code,
      enabled: this.fuelPointDataForm.controls.enabled.value ?? false,
      pumpNumber: this.fuelPointDataForm.controls.pumpNumber.value ?? null,
      serialNumber: this.fuelPointDataForm.controls.serialNumber.value ?? null,
      description: this.fuelPointDataForm.controls.description.value ?? null,
      operationModes: this.setOperationModes(this.fuelPointDataForm),
      protocolTypeCode:
        this.fuelPointDataForm.controls.protocolTypeCode.value ?? null,
      nozzles:
        this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.nozzles).value ?? null,
      communicationMethodTypeCode:
        protocolFormGroup.get(PROTOCOL_FORM_PARAMS.communicationMethodTypeCode)
          ?.value ||
        this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
          .value === FccPmpCfgProfiles[1].code ||
        this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
          .value === FccPmpCfgProfiles[7].code
          ? CommunicationMethodTypeCode.SERIAL_CONNECTION
          : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData:
        this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
          .value === FccPmpCfgProfiles[1].code ||
        this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.protocolTypeCode)
          .value === FccPmpCfgProfiles[7].code
          ? this.setPetrotecLegacyMethodData(this.fuelPointDataForm)
          : setCommunicationMethodData(protocolFormGroup),
    };

    if (this.isCreate) {
      this.store.dispatch(createFuelPoint({ fuelpoint }));
    } else {
      this.store.dispatch(updateFuelPoint({ fuelpoint }));
    }

    this.fuelPointDataForm.markAsUntouched();
    this.fuelPointDataForm.markAsPristine();
    this.getFuelPointFormPristine();
  }

  invalidNozzles(form: FormGroup): boolean {
    const nozzles = form.get(FUELPOINT_FORM_PARAMS.nozzles).value;

    return nozzles.some((nozzleEl: NozzleDTO) => {
      const filteredFuelPoints = nozzles.filter(
        (nozzle: NozzleDTO) => nozzle.nozzleNumber === nozzleEl.nozzleNumber
      );
      return filteredFuelPoints.length > 1;
    });
  }

  setPetrotecLegacyMethodData(formGroup: FormGroup) {
    const pmpTypeProfileId = formGroup.get(
      FUELPOINT_FORM_PARAMS.pmpTypeProfile
    ).value;
    let profileBaudRate: number;
    switch (
      this.fuelPointDataForm.get(FUELPOINT_FORM_PARAMS.protocolTypeCode).value
    ) {
      case FccPmpCfgProfiles[1].code:
        profileBaudRate = this.fccPmpCfgProfiles[1].profile.find(
          ({ id }) => id === pmpTypeProfileId
        )?.baudRate;
        break;
      case FccPmpCfgProfiles[7].code:
        profileBaudRate = this.fccPmpCfgProfiles[7].profile.find(
          ({ id }) => id === pmpTypeProfileId
        )?.baudRate;
        break;
      default:
        break;
    }
    return {
      address: pmpTypeProfileId ?? null,
      baudRate: profileBaudRate ?? null,
    };
  }

  setOperationModes(formGroup: FormGroup) {
    const operationModeTokens = formGroup.get(
      FUELPOINT_FORM_PARAMS.operationModes
    ).value;
    return operationModeTokens
      ? operationModeTokens.map(({ token }) => ({ token }))
      : null;
  }

  getFuelPointFormPristine() {
    this.getFuelPointForm.emit(this.fuelPointDataForm);
  }

  compareOperationModes(o1: OperationModeDTO, o2: OperationModeDTO): boolean {
    return o1 && o2 ? o1.token === o2.token : o1 === o2;
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.fuelPointDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }

  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
